import { computed, readonly, ref, shallowRef } from "vue";
import { deleteAnalyticsCookies, readConsent, writeConsent } from "../utils";
import type { CookieConsentType } from "../types";

/** The visitor's choice, shared by the whole app; nothing until they choose */
const consent = shallowRef<CookieConsentType | undefined>(readConsent());
const isBannerOpen = ref(!consent.value);

/** Waiting for the visitor to allow analytics cookies, see `onAnalyticsAllowed` */
const analyticsCallbacks: Array<() => void> = [];
/** Whether analytics tools were started on this page: they cannot be unloaded, only a reload gets rid of them */
let analyticsStarted = false;

function runAnalyticsCallbacks() {
  analyticsStarted = analyticsStarted || analyticsCallbacks.length > 0;
  analyticsCallbacks.splice(0).forEach((callback) => callback());
}

/**
 * Tells the Google tags what they may store. Advertising is never used, so it stays denied (see index.html, which
 * applies the stored choice before any tag runs).
 */
function updateGoogleConsent(value: CookieConsentType) {
  window.gtag?.("consent", "update", { analytics_storage: value.analytics ? "granted" : "denied" });
}

/**
 * The cookie banner: what the visitor allows, stored in a cookie of this site (see `CONSENT_COOKIE_NAME`).
 * Analytics tools must only be started through `onAnalyticsAllowed`.
 */
export function useCookieConsent() {
  const isAnalyticsAllowed = computed(() => consent.value?.analytics === true);

  function save(analytics: boolean) {
    const value: CookieConsentType = { strictly_necessary: true, analytics };

    writeConsent(value);
    consent.value = value;
    updateGoogleConsent(value);
    isBannerOpen.value = false;

    if (analytics) {
      runAnalyticsCallbacks();
      return;
    }

    deleteAnalyticsCookies();

    // Once loaded, Google Analytics keeps sending cookieless pings with the consent denied, and Hotjar keeps recording
    if (analyticsStarted) {
      window.location.reload();
    }
  }

  function openBanner() {
    isBannerOpen.value = true;
  }

  /** Only once the visitor has chosen: until then the banner stays */
  function closeBanner() {
    if (consent.value) {
      isBannerOpen.value = false;
    }
  }

  /**
   * Runs the callback once analytics cookies are allowed: right away if they already are, otherwise when the visitor
   * allows them
   */
  function onAnalyticsAllowed(callback: () => void) {
    if (isAnalyticsAllowed.value) {
      analyticsStarted = true;
      callback();
    } else {
      analyticsCallbacks.push(callback);
    }
  }

  return {
    consent: computed(() => consent.value),
    hasChosen: computed(() => !!consent.value),
    isAnalyticsAllowed,
    isBannerOpen: readonly(isBannerOpen),
    acceptAll: () => save(true),
    rejectOptional: () => save(false),
    save,
    openBanner,
    closeBanner,
    onAnalyticsAllowed,
  };
}
